import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Icon from '../../components/AppIcon';
import ProfileTab from './components/ProfileTab';
import FinancialProfileTab from './components/FinancialProfileTab';
import NotificationsTab from './components/NotificationsTab';
import SecurityTab from './components/SecurityTab';
import SubscriptionTab from './components/SubscriptionTab';

const TABS = [
  { id: 'profile', label: 'Profile', icon: 'User', mobileLabel: 'Profile' },
  { id: 'financial', label: 'Financial Profile', icon: 'TrendingUp', mobileLabel: 'Financial' },
  { id: 'notifications', label: 'Notifications & Alerts', icon: 'Bell', mobileLabel: 'Alerts' },
  { id: 'security', label: 'Security', icon: 'Shield', mobileLabel: 'Security' },
  { id: 'subscription', label: 'Subscription', icon: 'Crown', mobileLabel: 'Plan' },
];

function Toast({ toast, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const isSuccess = toast?.type === 'success';
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl animate-in slide-in-from-right-5"
      style={{
        background: isSuccess ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
        border: `1px solid ${isSuccess ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
        backdropFilter: 'blur(12px)',
        minWidth: '280px',
      }}
    >
      <Icon name={isSuccess ? 'CheckCircle' : 'XCircle'} size={16} color={isSuccess ? '#22C55E' : '#EF4444'} />
      <span className="text-sm font-medium" style={{ color: isSuccess ? '#22C55E' : '#EF4444' }}>{toast?.message}</span>
      <button onClick={onClose} className="ml-auto" style={{ color: '#64748B' }}>
        <Icon name="X" size={13} color="currentColor" />
      </button>
    </div>
  );
}

export default function SettingsProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const tab = params?.get('tab');
    if (tab && TABS?.find(t => t?.id === tab)) setActiveTab(tab);
  }, [location?.search]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/settings-profile?tab=${tabId}`, { replace: true });
  };

  const addToast = (toast) => {
    const id = Date.now();
    setToasts(prev => [...prev, { ...toast, id }]);
  };

  const removeToast = (id) => setToasts(prev => prev?.filter(t => t?.id !== id));

  const activeTabData = TABS?.find(t => t?.id === activeTab);

  return (
    <div className="min-h-screen" style={{ background: '#0D1B2A' }}>
      <PrimaryNavigation />
      {/* Toast Container */}
      <div className="fixed top-20 right-4 z-[200] flex flex-col gap-2">
        {toasts?.map(toast => (
          <Toast key={toast?.id} toast={toast} onClose={() => removeToast(toast?.id)} />
        ))}
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-24 md:pb-8 pt-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.2), rgba(123,47,190,0.2))', border: '1px solid rgba(0,229,255,0.2)' }}>
              <Icon name="Settings" size={18} color="#00E5FF" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Settings & Profile</h1>
              <p className="text-sm" style={{ color: '#64748B' }}>Manage your account, preferences, and security</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Tabs — Desktop */}
          <aside className="hidden lg:flex flex-col gap-1 w-56 flex-shrink-0">
            {TABS?.map(tab => {
              const active = activeTab === tab?.id;
              return (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 text-left hover:scale-[1.01]"
                  style={{
                    background: active ? 'rgba(0,229,255,0.1)' : 'transparent',
                    border: `1px solid ${active ? 'rgba(0,229,255,0.25)' : 'transparent'}`,
                    color: active ? '#00E5FF' : '#94A3B8',
                    boxShadow: active ? '0 0 12px rgba(0,229,255,0.1)' : 'none',
                  }}
                >
                  <Icon name={tab?.icon} size={16} color={active ? '#00E5FF' : '#64748B'} />
                  {tab?.label}
                  {active && <Icon name="ChevronRight" size={14} color="#00E5FF" className="ml-auto" />}
                </button>
              );
            })}
          </aside>

          {/* Mobile Tab Bar */}
          <div className="lg:hidden flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
            {TABS?.map(tab => {
              const active = activeTab === tab?.id;
              return (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-200"
                  style={{
                    background: active ? 'rgba(0,229,255,0.12)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${active ? 'rgba(0,229,255,0.3)' : 'rgba(255,255,255,0.06)'}`,
                    color: active ? '#00E5FF' : '#94A3B8',
                  }}
                >
                  <Icon name={tab?.icon} size={13} color={active ? '#00E5FF' : '#64748B'} />
                  {tab?.mobileLabel}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <main className="flex-1 min-w-0">
            <div
              className="p-6 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)', backdropFilter: 'blur(12px)' }}
            >
              {/* Tab Header */}
              <div className="flex items-center gap-3 mb-6 pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,229,255,0.1)' }}>
                  <Icon name={activeTabData?.icon || 'Settings'} size={16} color="#00E5FF" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">{activeTabData?.label}</h2>
                </div>
              </div>

              {/* Render Active Tab */}
              {activeTab === 'profile' && <ProfileTab onToast={addToast} />}
              {activeTab === 'financial' && <FinancialProfileTab onToast={addToast} />}
              {activeTab === 'notifications' && <NotificationsTab onToast={addToast} />}
              {activeTab === 'security' && <SecurityTab onToast={addToast} />}
              {activeTab === 'subscription' && <SubscriptionTab onToast={addToast} />}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
